/**
 * Small markdown → HTML helper shared by Help and the Roadmap document preview.
 * Escapes raw HTML first so composed card docs cannot inject markup.
 */

function slugify(text) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function parseInline(text) {
  let r = escapeHtml(text);
  r = r.replace(/`([^`]+)`/g, '<code>$1</code>');
  r = r.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img alt="$1" src="$2" />');
  r = r.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  r = r.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  r = r.replace(/__(.+?)__/g, '<strong>$1</strong>');
  r = r.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
  r = r.replace(/(?<!_)_(?!_)(.+?)(?<!_)_(?!_)/g, '<em>$1</em>');
  return r;
}

function parseMarkdown(md) {
  const lines = String(md || '').split('\n');
  const html = [];
  const toc = [];
  let i = 0;
  let inCB = false;
  let cbContent = [];
  let inTbl = false;
  let tblRows = [];
  let inBQ = false;
  let bqLines = [];
  let inList = false;
  let liItems = [];
  let liOrd = false;

  const flushBQ = () => {
    if (!inBQ) return;
    html.push(`<blockquote>${bqLines.map((l) => parseInline(l)).join('<br/>')}</blockquote>`);
    bqLines = [];
    inBQ = false;
  };
  const flushTbl = () => {
    if (!inTbl) return;
    let t = '<div class="help-table-wrapper"><table>';
    tblRows.forEach((row, idx) => {
      if (idx === 1 && /^[\s|:-]+$/.test(row)) return;
      const cells = row.replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
      const tag = idx === 0 ? 'th' : 'td';
      if (idx === 0) t += '<thead>';
      if (idx === 2) t += '<tbody>';
      t += `<tr>${cells.map((c) => `<${tag}>${parseInline(c)}</${tag}>`).join('')}</tr>`;
      if (idx === 0) t += '</thead>';
    });
    if (tblRows.length > 2) t += '</tbody>';
    t += '</table></div>';
    html.push(t);
    tblRows = [];
    inTbl = false;
  };
  const flushList = () => {
    if (!inList) return;
    const tag = liOrd ? 'ol' : 'ul';
    html.push(`<${tag}>${liItems.map((item) => `<li>${parseInline(item)}</li>`).join('')}</${tag}>`);
    liItems = [];
    inList = false;
  };

  while (i < lines.length) {
    const line = lines[i];
    if (line.trimStart().startsWith('```')) {
      if (inCB) {
        html.push(`<pre><code>${escapeHtml(cbContent.join('\n'))}</code></pre>`);
        cbContent = [];
        inCB = false;
      } else {
        flushBQ();
        flushTbl();
        flushList();
        inCB = true;
      }
      i += 1;
      continue;
    }
    if (inCB) {
      cbContent.push(line);
      i += 1;
      continue;
    }
    if (line.trim() === '') {
      flushBQ();
      flushTbl();
      flushList();
      i += 1;
      continue;
    }
    if (/^---+$/.test(line.trim()) || /^\*\*\*+$/.test(line.trim())) {
      flushBQ();
      flushTbl();
      flushList();
      html.push('<hr/>');
      i += 1;
      continue;
    }
    const hm = line.match(/^(#{1,6})\s+(.+)/);
    if (hm) {
      flushBQ();
      flushTbl();
      flushList();
      const lv = hm[1].length;
      const txt = hm[2].trim();
      const id = slugify(txt);
      if (lv <= 3) toc.push({ level: lv, text: txt, id });
      html.push(`<h${lv} id="${id}">${parseInline(txt)}</h${lv}>`);
      i += 1;
      continue;
    }
    if (line.trim().startsWith('|') || (line.includes('|') && line.trim().match(/^\|.*\|$/))) {
      flushBQ();
      flushList();
      inTbl = true;
      tblRows.push(line.trim());
      i += 1;
      continue;
    }
    if (inTbl) {
      flushTbl();
      continue;
    }
    if (line.trimStart().startsWith('> ')) {
      flushTbl();
      flushList();
      inBQ = true;
      bqLines.push(line.replace(/^>\s?/, ''));
      i += 1;
      continue;
    }
    if (inBQ) {
      flushBQ();
      continue;
    }
    const ulm = line.match(/^(\s*)[-*]\s+(.+)/);
    if (ulm) {
      flushBQ();
      flushTbl();
      if (!inList || liOrd) {
        flushList();
        inList = true;
        liOrd = false;
      }
      liItems.push(ulm[2]);
      i += 1;
      continue;
    }
    const olm = line.match(/^(\s*)\d+\.\s+(.+)/);
    if (olm) {
      flushBQ();
      flushTbl();
      if (!inList || !liOrd) {
        flushList();
        inList = true;
        liOrd = true;
      }
      liItems.push(olm[2]);
      i += 1;
      continue;
    }
    if (inList) flushList();
    html.push(`<p>${parseInline(line)}</p>`);
    i += 1;
  }
  if (inCB) html.push(`<pre><code>${escapeHtml(cbContent.join('\n'))}</code></pre>`);
  flushBQ();
  flushTbl();
  flushList();
  return { html: html.join('\n'), toc };
}

export { parseMarkdown, escapeHtml };
